import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CrudRowsService, CrudService, ENVIRONMENT, FileUploadService, HateoasMultipleResponseObject, PageSpecification, REPOSITORY_FILE_TYPE, RepositoryFile, RepositoryFileReference, TokenStorage, UploadFile, appendSerialisedHttpParams, markAsBackgroundServiceCall, urlWithParams } from '@scisys-uim/uim-common';
import { FirearmDocumentType } from 'assets-model';
import { Observable } from 'rxjs';

export type FirearmDocumentPermission = 'READ' | 'UPLOAD';

@Injectable({
  providedIn: 'root'
})
export class FirearmDocumentService extends CrudService<RepositoryFile, RepositoryFileReference> {

  servicePath: string = '/firearm-document';

  constructor(protected http: HttpClient,
    protected fileUploadService: FileUploadService,
    @Inject(ENVIRONMENT) protected environment: any,
    private tokenStorage: TokenStorage) {
    super(http, environment, REPOSITORY_FILE_TYPE.entityDecorator);
  }

  getBlacklistedContentTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/blacklistedContentTypes');
  }
  getBlacklistedExtensions(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/blacklistedExtensions');
  }
  getWhitelistedContentTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/whitelistedContentTypes');
  }
  getWhitelistedExtensions(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/whitelistedExtensions');
  }

  getFirearmPermissions(assetId: number, docType: FirearmDocumentType): Observable<FirearmDocumentPermission[]> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.http.get<FirearmDocumentPermission[]>(this.serviceBaseUrl + '/getFirearmDocumentPermissions/' + assetId, {
      params: params
    });
  }

  protected uploadFile(file: UploadFile, servicePath: string, docType: FirearmDocumentType): Observable<HttpEvent<RepositoryFile>> {
    var params: HttpParams = appendSerialisedHttpParams(docType, 'docType');
    return this.fileUploadService.uploadRepositoryFile(file, servicePath, params);
  }

  findFirearmDocuments(assetId: number, docType: FirearmDocumentType): Observable<HateoasMultipleResponseObject<RepositoryFile>> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.deserialiseMultipleResponseListAsHateoas(this.http.get<RepositoryFile[]>(this.serviceBaseUrl + '/findAllFirearmDocuments/' + assetId, {
      params: params
    }));
  }

  countFirearmDocuments(assetId: number, docType: FirearmDocumentType): Observable<number> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.http.get<number>(this.serviceBaseUrl + '/countAllFirearmDocuments/' + assetId, {
      params: params
    });
  }

  exportFirearmDocumentByFilename(assetId: number, docType: FirearmDocumentType, file: RepositoryFile, download?: boolean): string {
    var params = appendSerialisedHttpParams(!!download, "download");
    params = appendSerialisedHttpParams(file.filePath, "filename", params);
    params = appendSerialisedHttpParams(docType, "docType", params);
    params = this.tokenStorage.appendAuthenticationHttpParams(params);
    return urlWithParams(this.serviceBaseUrl + '/exportFirearmDocumentByFilename/' + assetId, params);
  }

  exportMultipleFirearmDocumentsAsZipByFileSpecification(assetId: number, docType: FirearmDocumentType): string {
    var params = appendSerialisedHttpParams(params);
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    params = this.tokenStorage.appendAuthenticationHttpParams(params);
    return urlWithParams(this.serviceBaseUrl + '/exportMultipleFirearmDocumentsAsZipByFileSpecification/' + assetId, params);
  }

  uploadFirearmDocument(assetId: number, docType: FirearmDocumentType, file: UploadFile): Observable<HttpEvent<RepositoryFile>> {
    return this.uploadFile(file, this.serviceBaseUrl + "/uploadFirearmDocument/" + assetId, docType);
  }

  deleteFirearmDocument(file: RepositoryFile) {
    var params = appendSerialisedHttpParams(file.filePath, 'filename');
    params = appendSerialisedHttpParams(file.userAttributes.firearmDocumentType, "docType", params);
    return this.http.delete(this.serviceBaseUrl + '/deleteFirearmDocument/' + file.userAttributes.assetId, {
      params: params
    });
  }

  public get(key: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public findAll(): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public findAllPaginated(pageSpec: PageSpecification): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public existsById(id: RepositoryFileReference): Observable<boolean> { throw new Error('Not implemented'); }
  public create(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public save(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public persistAll(rows: RepositoryFile[]): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public delete(id: RepositoryFileReference): Observable<void> { throw new Error('Not implemented'); }

}

@Injectable({
  providedIn: 'root'
})
export class FirearmDocumentRowsService extends CrudService<RepositoryFile, RepositoryFileReference> implements CrudRowsService<RepositoryFile, RepositoryFileReference, any> {

  servicePath: string = null;

  constructor(protected service: FirearmDocumentService) {
    super(null, null, REPOSITORY_FILE_TYPE.entityDecorator);
  }

  findAllDocuments(assetId: number, docType: FirearmDocumentType): Observable<HateoasMultipleResponseObject<RepositoryFile>> {
    return this.service.findFirearmDocuments(assetId, docType);
  }

  exportDocumentByFilename(assetId: number, docType: FirearmDocumentType, file: RepositoryFile, download?: boolean) {
    return this.service.exportFirearmDocumentByFilename(assetId, docType, file, download);
  }

  exportMultipleDocumentsAsZipByFileSpecification(assetId: number, docType: FirearmDocumentType): string {
    return this.service.exportMultipleFirearmDocumentsAsZipByFileSpecification(assetId, docType);
  }

  uploadFile(assetId: number, docType: FirearmDocumentType, file: UploadFile): Observable<HttpEvent<RepositoryFile>> {
    return this.service.uploadFirearmDocument(assetId, docType, file);
  }

  deleteDocumentByFilename(file: RepositoryFile) {
    return this.service.deleteFirearmDocument(file);
  }

  public findAllInGroup(key: any): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public get(key: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public findAll(): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public findAllPaginated(pageSpec: PageSpecification): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public existsById(id: RepositoryFile): Observable<boolean> { throw new Error('Not implemented'); }
  public create(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public save(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public persistAll(rows: RepositoryFile[]): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public delete(id: RepositoryFileReference): Observable<void> { throw new Error('Not implemented'); }
}

